import * as yup from 'yup';
import * as IBAN from 'iban';
import { evalCondition, extractDepFields } from './useConditional.js';

yup.addMethod(yup.string, 'iban', function (message) {
  return this.test('iban', message || 'Ungültige IBAN', (v) => !v || IBAN.isValid(v));
});

yup.addMethod(yup.string, 'ihk', function (message) {
  return this.test(
    'ihk',
    message || 'Ungültige IHK-Registernummer',
    (v) => !v || /^D-[A-Z0-9]{4}-[A-Z0-9]{5}-[A-Z0-9]{2}$/.test(v)
  );
});

const RULE_BUILDERS = {
  required:       (s, r) => s.required(r.message || 'Pflichtfeld'),
  min:            (s, r) => s.min(r.value, r.message),
  max:            (s, r) => s.max(r.value, r.message),
  max_date_today: (s, r) => s.max(new Date(), r.message),
  email:          (s, r) => s.email(r.message || 'Ungültige E-Mail-Adresse'),
  iban:           (s, r) => s.iban(r.message),
  ihk:            (s, r) => s.ihk(r.message),
  starts_with:    (s, r) =>
    s.test('starts_with', r.message || `Muss mit ${r.values} beginnen`, (v) =>
      !v || r.values.some((p) => v.startsWith(p))
    ),
  required_other: (s, r) =>
    s.test('required_other', r.message || 'Pflichtfeld', (_, ctx) => !!ctx.parent[r.field]),
  fields_equal:   (s, r) =>
    s.test('fields_equal', r.message || 'Felder sind ungleich', (v, ctx) => v === ctx.parent[r.other]),
  required_when:  (s, r) =>
    s.when(r.field, { is: r.equals, then: (sc) => sc.required(r.message) }),
  must_be_true:   (s, r) => s.oneOf([true], r.message || 'Pflichtfeld'),
};

function inferBaseType(control) {
  switch (control) {
    case 'switch':
    case 'checkbox':
      return yup.boolean();
    case 'numberinput':
    case 'sliderinput':
    case 'twolabelslider':
      return yup.number();
    case 'datepicker':
      return yup.date();
    case 'upload':
    case 'expandable':
    case 'claim_costs_editor':
    case 'claim_attachments':
    case 'coverage_type_questions':
      return yup.array();
    default:
      return yup.string();
  }
}

// A field hidden by `hidden: {when: ...}` can't be filled in by the user, so
// its `required` rule must not block the field being empty while hidden -
// otherwise the form can never become valid (e.g. formA_new's fahrer_* fields,
// hidden whenever fahrer_identisch_halter is true, its own default).
function requiredUnlessHidden(schema, hidden, message) {
  const depFields = [...new Set(extractDepFields(hidden))];
  if (depFields.length === 0) {
    return evalCondition(hidden, {}) ? schema : schema.required(message);
  }
  return schema.when(depFields, (values, s) => {
    const model = Object.fromEntries(depFields.map((f, i) => [f, values[i]]));
    return evalCondition(hidden, model) ? s : s.required(message);
  });
}

function buildFieldSchema(item) {
  const base = inferBaseType(item.control);
  return (item.valid ?? [])
    .reduce((schema, rule) => {
      if (rule.fn === 'required' && item.hidden) {
        return requiredUnlessHidden(schema, item.hidden, rule.message || 'Pflichtfeld');
      }
      return RULE_BUILDERS[rule.fn]?.(schema, rule) ?? schema;
    }, base)
    .nullable();
}

export function buildFormSchema(flatItems) {
  return yup.object(
    Object.fromEntries(
      flatItems
        .filter((item) => item.name && item.valid?.length)
        .map((item) => [item.name, buildFieldSchema(item)])
    )
  );
}
